/** @format */

import { Button, Slider, Tooltip } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

/*
 * Scroll orizzontale del report presenze pensato anche per chi usa il mouse:
 *
 *  1. tutte le tabelle dei dipendenti scorrono insieme (scroll sincronizzato);
 *  2. si può trascinare la tabella con il tasto sinistro (drag-to-scroll);
 *  3. una barra fissa in alto offre frecce, salto al mese e cursore di scorrimento;
 *  4. restano valide le modalità native: Maiusc + rotellina, trackpad,
 *     frecce da tastiera con la tabella a fuoco, barra di scorrimento sempre visibile.
 */

type Listener = () => void;

interface ScrollSyncApi {
  register: (el: HTMLElement) => () => void;
  scrollTo: (left: number, smooth?: boolean) => void;
  subscribe: (listener: Listener) => () => void;
  getPrimary: () => HTMLElement | undefined;
}

const ScrollSyncContext = createContext<ScrollSyncApi | null>(null);

// Spostamento minimo (px) prima che un click diventi trascinamento
const DRAG_THRESHOLD = 5;

// Giorni di cui si sposta la vista con le frecce
const DAYS_PER_STEP = 7;

/* ------------------------------------------------------------------ */
/* Provider                                                           */
/* ------------------------------------------------------------------ */

export const ReportScrollProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const storeRef = useRef({
    scrollers: new Set<HTMLElement>(),
    listeners: new Set<Listener>(),
    left: 0,
    frame: 0,
  });

  const api = useMemo<ScrollSyncApi>(() => {
    const store = storeRef.current;

    // Notifica la barra di navigazione al massimo una volta per frame
    const notify = () => {
      if (store.frame) return;
      store.frame = requestAnimationFrame(() => {
        store.frame = 0;
        store.listeners.forEach(listener => listener());
      });
    };

    const handleScroll = (event: Event) => {
      const source = event.currentTarget as HTMLElement;
      const left = source.scrollLeft;

      // Eco di una sincronizzazione: il valore coincide già con quello condiviso
      if (Math.abs(left - store.left) < 1) return;

      store.left = left;
      store.scrollers.forEach(el => {
        if (el !== source && Math.abs(el.scrollLeft - left) >= 1) el.scrollLeft = left;
      });
      notify();
    };

    return {
      register: el => {
        store.scrollers.add(el);
        // una tabella montata dopo (LazyList) parte dalla posizione corrente
        el.scrollLeft = store.left;
        el.addEventListener("scroll", handleScroll, { passive: true });
        notify();

        return () => {
          el.removeEventListener("scroll", handleScroll);
          store.scrollers.delete(el);
          notify();
        };
      },

      scrollTo: (left, smooth = true) => {
        const primary = store.scrollers.values().next().value as HTMLElement | undefined;
        if (!primary) return;

        const max = primary.scrollWidth - primary.clientWidth;
        const target = Math.max(0, Math.min(max, left));

        // si muove solo la prima tabella: le altre la seguono tramite handleScroll
        if (smooth) primary.scrollTo({ left: target, behavior: "smooth" });
        else primary.scrollLeft = target;
      },

      subscribe: listener => {
        store.listeners.add(listener);
        return () => {
          store.listeners.delete(listener);
        };
      },

      getPrimary: () => store.scrollers.values().next().value as HTMLElement | undefined,
    };
  }, []);

  useEffect(() => {
    const store = storeRef.current;
    return () => {
      if (store.frame) cancelAnimationFrame(store.frame);
    };
  }, []);

  return <ScrollSyncContext.Provider value={api}>{children}</ScrollSyncContext.Provider>;
};

/* ------------------------------------------------------------------ */
/* Hook per il contenitore scrollabile di ogni tabella                */
/* ------------------------------------------------------------------ */

/**
 * Restituisce una callback ref da assegnare al contenitore con overflow-x.
 * Funziona anche senza provider: in quel caso abilita solo il trascinamento.
 */
export const useSyncedScroll = (): ((el: HTMLDivElement | null) => void) => {
  const api = useContext(ScrollSyncContext);
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (el: HTMLDivElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!el) return;

      const unregister = api ? api.register(el) : undefined;
      const disableDrag = enableDragToScroll(el);

      cleanupRef.current = () => {
        if (unregister) unregister();
        disableDrag();
      };
    },
    [api],
  );
};

/**
 * Trascinamento orizzontale con il tasto sinistro del mouse.
 * Parte solo dopo DRAG_THRESHOLD px, così click e tooltip continuano a funzionare.
 */
const enableDragToScroll = (el: HTMLElement): (() => void) => {
  let pressed = false;
  let dragging = false;
  let startX = 0;
  let startLeft = 0;

  const updateCanDrag = () => {
    el.classList.toggle("can-drag", el.scrollWidth > el.clientWidth + 1);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!pressed) return;
    const dx = e.clientX - startX;

    if (!dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      dragging = true;
      el.classList.add("is-dragging");
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
    }

    e.preventDefault();
    el.scrollLeft = startLeft - dx;
  };

  const stopClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseUp = () => {
    pressed = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);

    if (dragging) {
      dragging = false;
      el.classList.remove("is-dragging");
      // il rilascio dopo un trascinamento non deve valere come click
      window.addEventListener("click", stopClick, true);
      setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
    }
  };

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0 || el.scrollWidth <= el.clientWidth + 1) return;

    // click sulla barra di scorrimento nativa: la lascio al browser
    if (e.clientY - el.getBoundingClientRect().top > el.clientHeight) return;

    pressed = true;
    startX = e.clientX;
    startLeft = el.scrollLeft;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  updateCanDrag();
  el.addEventListener("mousedown", onMouseDown);

  const resizeObserver =
    typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateCanDrag) : undefined;
  if (resizeObserver) resizeObserver.observe(el);
  else window.addEventListener("resize", updateCanDrag);

  return () => {
    el.removeEventListener("mousedown", onMouseDown);
    if (resizeObserver) resizeObserver.disconnect();
    else window.removeEventListener("resize", updateCanDrag);
    onMouseUp();
  };
};

/* ------------------------------------------------------------------ */
/* Barra di navigazione orizzontale                                   */
/* ------------------------------------------------------------------ */

interface MonthAnchor {
  key: string;
  label: string;
  offset: number;
}

interface ScrollMetrics {
  left: number;
  max: number;
  dayWidth: number;
  months: MonthAnchor[];
}

// Le posizioni vengono lette dal DOM della prima tabella: nessun calcolo duplicato
const readMetrics = (el: HTMLElement): ScrollMetrics => {
  const max = Math.max(0, el.scrollWidth - el.clientWidth);
  const labelCell = el.querySelector<HTMLElement>("thead th.label");
  const labelWidth = labelCell ? labelCell.offsetWidth : 0;
  const dayCell = el.querySelector<HTMLElement>("thead tr:nth-child(2) th");
  const dayWidth = dayCell ? dayCell.offsetWidth : 44;

  const monthCells = Array.from(el.querySelectorAll<HTMLElement>("thead th[data-month]"));
  const useShortLabels = monthCells.length > 4;

  const months = monthCells.map(th => ({
    key: th.dataset.month || "",
    label: (useShortLabels ? th.dataset.monthShort : th.dataset.monthLabel) || "",
    offset: Math.min(max, Math.max(0, th.offsetLeft - labelWidth)),
  }));

  return { left: el.scrollLeft, max, dayWidth, months };
};

interface ReportScrollNavProps {
  /** distanza dal bordo superiore quando la barra resta fissa (es. altezza di un header fisso) */
  stickyTop?: number;
}

export const ReportScrollNav = ({ stickyTop = 0 }: ReportScrollNavProps): JSX.Element | null => {
  const api = useContext(ScrollSyncContext);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!api) return undefined;
    const rerender = () => setTick(n => n + 1);
    const unsubscribe = api.subscribe(rerender);
    window.addEventListener("resize", rerender);
    return () => {
      unsubscribe();
      window.removeEventListener("resize", rerender);
    };
  }, [api]);

  if (!api) return null;

  const primary = api.getPrimary();
  if (!primary) return null;

  const { left, max, dayWidth, months } = readMetrics(primary);

  // la tabella entra tutta nello schermo: la barra non serve
  if (max <= 0) return null;

  const step = DAYS_PER_STEP * dayWidth;
  const activeMonth = months.reduce<string | undefined>(
    (active, m) => (m.offset <= left + 2 ? m.key : active),
    months.length ? months[0].key : undefined,
  );

  return (
    <NavBar style={{ top: stickyTop }}>
      <Tooltip title="Settimana precedente">
        <Button
          size="small"
          icon={<LeftOutlined />}
          disabled={left <= 0}
          onClick={() => api.scrollTo(left - step)}
          aria-label="Settimana precedente"
        />
      </Tooltip>

      {months.length > 1 && (
        <MonthButtons>
          {months.map(m => (
            <Button
              key={m.key}
              size="small"
              type={m.key === activeMonth ? "primary" : "default"}
              onClick={() => api.scrollTo(m.offset)}
            >
              {m.label}
            </Button>
          ))}
        </MonthButtons>
      )}

      <SliderWrap>
        <Slider
          min={0}
          max={Math.round(max)}
          value={Math.round(left)}
          tooltipVisible={false}
          onChange={(value: number) => api.scrollTo(value, false)}
        />
      </SliderWrap>

      <Tooltip title="Settimana successiva">
        <Button
          size="small"
          icon={<RightOutlined />}
          disabled={left >= max - 1}
          onClick={() => api.scrollTo(left + step)}
          aria-label="Settimana successiva"
        />
      </Tooltip>

      <Hint>Puoi anche trascinare la tabella o usare Maiusc + rotellina</Hint>
    </NavBar>
  );
};

/* ------------------------------------------------------------------ */
/* Stili                                                              */
/* ------------------------------------------------------------------ */

const NavBar = styled.div`
  position: sticky;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(38, 38, 38, 0.06);

  @media print {
    display: none;
  }
`;

const MonthButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const SliderWrap = styled.div`
  flex: 1 1 200px;
  min-width: 160px;

  .ant-slider {
    margin: 6px 4px;
  }
`;

const Hint = styled.span`
  flex-basis: 100%;
  font-size: 12px;
  color: #8c8c8c;
`;
